/* =========================================================================
 * GP — CRT Background (background-only, reusable)
 * - Uses your existing shader approach, minus all interactions
 * - Reads 0–100 UI sliders from data attrs (same ranges as your original)
 * - No magnifier, no per-card palette, no hue detection
 * ========================================================================= */
(() => {
  function onReady(fn){
    if (document.readyState === 'complete' || document.readyState === 'interactive') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  onReady(() => {
    if (!window.THREE) return;
    document.querySelectorAll('[data-gp-crt]').forEach(initSection);
  });

  function initSection(root){
    if (root.__gpCrtInit) return;
    root.__gpCrtInit = true;

    const opts = readOptions(root);
    const canvas = ensureCanvas(root, opts.hideOnMobile);
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    renderer.setPixelRatio(Math.min(DPR, 1.75));
    sizeRenderer(renderer);

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1, 1);
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), buildMaterial(opts));
    scene.add(quad);

    const clock = new THREE.Clock();
    function tick(){
      quad.material.uniforms.time.value = clock.getElapsedTime();
      renderer.render(scene, camera);
      requestAnimationFrame(tick);
    }
    tick();

    window.addEventListener('resize', () => {
      sizeRenderer(renderer);
      quad.material.uniforms.aspect.value = window.innerWidth / window.innerHeight;
    });
  }

  function ensureCanvas(root, hideOnMobile){
    const id = `gp-crt-canvas-${root.id || Math.random().toString(36).slice(2)}`;
    let canvas = document.getElementById(id);
    if (!canvas){
      canvas = document.createElement('canvas');
      canvas.id = id;
      document.body.appendChild(canvas);
    }
    canvas.classList.add('gp-crt-canvas');
    if (hideOnMobile) canvas.classList.add('is-mobile-hidden');
    return canvas;
  }

  function sizeRenderer(renderer){
    renderer.setSize(window.innerWidth, window.innerHeight, false);
  }

  // ---- read + map 0–100 UI to shader ranges (same as your original) ----
  function read(root, key){ return root.getAttribute(`data-${key}`); }
  function readInt(root, key, def=0){ const v = parseInt(read(root, key) || '', 10); return isNaN(v) ? def : v; }
  function readBool(root, key){ return String(read(root, key)) === 'true'; }

  function readOptions(root){
    const lerp = (a,b,t)=>a+(b-a)*t;
    const t = (v)=>Math.min(1, Math.max(0, (v||0)/100));

    // Base UI pulls
    const scan_amp_ui   = readInt(root, 'scan-amp-ui', 40);
    const scan_speed_ui = readInt(root, 'scan-speed-ui', 10);
    const noise_amt_ui  = readInt(root, 'noise-amt-ui', 30);
    const rgb_shift_ui  = readInt(root, 'rgb-shift-ui', 22);

    const dot_scale_ui   = readInt(root, 'dot-scale-ui', 53);
    const dot_feather_ui = readInt(root, 'dot-feather-ui', 37);
    const dot_pulse_ui   = readInt(root, 'dot-pulse-ui', 33);
    const move_speed_ui  = readInt(root, 'move-speed-ui', 12);

    const curve_base_ui  = readInt(root, 'curve-base-ui', 40);
    const aniso_x_ui     = readInt(root, 'curve-aniso-x-ui', 50);
    const aniso_y_ui     = readInt(root, 'curve-aniso-y-ui', 35);
    const center_x_ui    = readInt(root, 'curve-center-x-ui', 65);
    const center_y_ui    = readInt(root, 'curve-center-y-ui', 55);
    const angle_ui       = readInt(root, 'curve-angle-ui', 59);
    const cnoise_amt_ui  = readInt(root, 'curve-noise-strength-ui', 40);
    const cnoise_scale_ui= readInt(root, 'curve-noise-scale-ui', 44);

    const static_amt_ui  = readInt(root, 'static-amt-ui', 42);
    const static_speed_ui= readInt(root, 'static-speed-ui', 36);

    // Map to shader ranges (preserved from your mapping)
    const opts = {
      scanAmp:   lerp(0.0, 0.6,  t(scan_amp_ui)),
      scanSpeed: lerp(0.0, 0.2,  t(scan_speed_ui)),
      noiseAmt:  lerp(0.0, 0.15, t(noise_amt_ui)),
      rgbShift:  lerp(0.0, 0.01, t(rgb_shift_ui)),

      dotScale:   Math.round(lerp(60, 400, t(dot_scale_ui))),
      dotFeather: lerp(0.02, 0.30, t(dot_feather_ui)),
      dotPulse:   lerp(0.05, 0.60, t(dot_pulse_ui)),
      moveSpeed:  lerp(0.0, 0.10, t(move_speed_ui)),

      curveBase:         lerp(0.0, 0.30, t(curve_base_ui)),
      curveAnisoX:       lerp(0.5, 1.5, t(aniso_x_ui)),
      curveAnisoY:       lerp(0.5, 1.5, t(aniso_y_ui)),
      curveCenterX:      lerp(0.45, 0.55, t(center_x_ui)),
      curveCenterY:      lerp(0.45, 0.55, t(center_y_ui)),
      curveAngle:        lerp(-0.2, 0.2, t(angle_ui)),
      curveNoiseStrength:lerp(0.0, 0.15, t(cnoise_amt_ui)),
      curveNoiseScale:   lerp(0.5, 3.0,  t(cnoise_scale_ui)),

      staticAmt:   lerp(0.0, 0.12, t(static_amt_ui)),
      staticSpeed: lerp(0.2, 3.0,  t(static_speed_ui)),

      initialTint: new THREE.Color(read(root, 'initial-tint') || '#9DFF6A'),
      initialBg:   new THREE.Color(read(root, 'initial-bg')   || '#071007'),

      hideOnMobile: readBool(root, 'hide-mobile')
    };
    return opts;
  }

  function buildMaterial(cfg){
    const uniforms = {
      time:   { value: 0 },
      aspect: { value: window.innerWidth / window.innerHeight },

      // CRT look
      scanAmp:    { value: cfg.scanAmp },
      scanSpeed:  { value: cfg.scanSpeed },
      noiseAmt:   { value: cfg.noiseAmt },
      rgbShift:   { value: cfg.rgbShift },

      // Halftone
      dotScale:   { value: cfg.dotScale },
      dotFeather: { value: cfg.dotFeather },
      dotPulse:   { value: cfg.dotPulse },
      moveSpeed:  { value: cfg.moveSpeed },

      // Colors
      bgColor:   { value: cfg.initialBg.clone() },
      tintColor: { value: cfg.initialTint.clone() },

      // Static
      staticAmt:   { value: cfg.staticAmt },
      staticSpeed: { value: cfg.staticSpeed },

      // Curvature
      curveBase:         { value: cfg.curveBase },
      curveAniso:        { value: new THREE.Vector2(cfg.curveAnisoX, cfg.curveAnisoY) },
      curveCenter:       { value: new THREE.Vector2(cfg.curveCenterX, cfg.curveCenterY) },
      curveAngle:        { value: cfg.curveAngle },
      curveNoiseStrength:{ value: cfg.curveNoiseStrength },
      curveNoiseScale:   { value: cfg.curveNoiseScale },

      // Legacy uniforms kept for compatibility (not used here)
      vignette: { value: 0.35 },
      zoom:     { value: 1.0 },
      jitterX:  { value: 0.0 }
    };

    return new THREE.ShaderMaterial({
      uniforms,
      vertexShader: `
        varying vec2 vUv;
        void main(){ vUv = uv; gl_Position = vec4(position, 1.0); }
      `,
      fragmentShader: `
        precision highp float;
        varying vec2 vUv;

        uniform float time, aspect;
        uniform float scanAmp, scanSpeed, noiseAmt, rgbShift;
        uniform float dotScale, dotFeather, dotPulse, moveSpeed;
        uniform vec3  bgColor, tintColor;

        uniform float staticAmt, staticSpeed;

        uniform float curveBase;
        uniform vec2  curveAniso;
        uniform vec2  curveCenter;
        uniform float curveAngle;
        uniform float curveNoiseStrength;
        uniform float curveNoiseScale;

        uniform float zoom;
        uniform float jitterX;
        uniform float vignette;

        float hash1(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453123); }
        float valueNoise(vec2 p){
          vec2 ip = floor(p), fp = fract(p);
          vec2 u = fp*fp*(3.0-2.0*fp);
          float a = hash1(ip);
          float b = hash1(ip + vec2(1.0,0.0));
          float c = hash1(ip + vec2(0.0,1.0));
          float d = hash1(ip + vec2(1.0,1.0));
          return mix(mix(a,b,u.x), mix(c,d,u.x), u.y);
        }
        float fbm(vec2 p){
          float s = 0.0, a = 0.5;
          for(int i=0;i<4;i++){ s += a * valueNoise(p); p *= 2.0; a *= 0.5; }
          return s;
        }
        vec2 rot(vec2 p, float a){ float s = sin(a), c = cos(a); return mat2(c,-s,s,c) * p; }

        vec2 crtWarp(vec2 uv){
          vec2 c = uv - curveCenter;
          c = rot(c, curveAngle);
          c.x *= aspect;
          float r2 = c.x*c.x*curveAniso.x + c.y*c.y*curveAniso.y;

          float n = fbm((uv * curveNoiseScale) + vec2(time*0.05, -time*0.037));
          float k = curveBase + (n - 0.5) * 2.0 * curveNoiseStrength;

          c *= (1.0 + k * r2);
          c.x /= aspect;
          c = rot(c, -curveAngle);
          c += curveCenter;
          return c;
        }

        vec3 halftone(vec2 uv){
          vec2 cuv = (uv - 0.5);
          cuv *= zoom; cuv += 0.5;
          cuv.x += jitterX;
          cuv += vec2(0.15*sin(time*moveSpeed*0.7), 0.12*cos(time*0.5*moveSpeed));

          vec2 g = cuv * dotScale;
          vec2 id = floor(g);
          vec2 f  = fract(g) - 0.5;
          float r = length(f) * 2.0;

          float phase = id.x*0.23 + id.y*0.17;
          float anim = 0.5 + 0.5 * sin(time*3.14159*moveSpeed + phase);
          float radius = clamp(dotPulse * (0.65 + 0.35*anim), 0.04, 0.95);

          float dotv = 1.0 - smoothstep(radius, radius + dotFeather, r);
          return mix(bgColor, tintColor, dotv);
        }

        void main(){
          vec2 uv = crtWarp(vUv);

          vec2 off = vec2(rgbShift, 0.0);
          vec3 c;
          c.r = halftone(uv + off).r;
          c.g = halftone(uv      ).g;
          c.b = halftone(uv - off).b;

          float scan = sin(uv.y * 3.14159 * 720.0 + time * scanSpeed) * scanAmp;
          c *= 1.0 - scan * 0.45;

          float film = (hash1(uv*vec2(640.0,360.0) + time*60.0) - 0.5) * 2.0 * noiseAmt;
          c += film;

          float gate = smoothstep(0.1, 0.9, 0.5 + 0.5 * sin(time * staticSpeed));
          float snow = (hash1(uv*vec2(1024.0,768.0) + floor(time*60.0)) - 0.5) * 2.0;
          float line = (hash1(vec2(floor(uv.y*720.0), floor(time*30.0))) - 0.5) * 2.0;
          c += (snow * 0.6 + line * 0.4) * staticAmt * gate;

          c *= 0.996 + 0.004 * sin(time * 10.0);

          float d = distance(uv, vec2(0.5));
          c *= smoothstep(0.96, 0.42 + vignette, 0.78 - d);

          gl_FragColor = vec4(c, 1.0);
        }
      `,
      transparent: true
    });
  }
})();

function ensureCanvas(root, hideOnMobile){
  const id = `gp-crt-canvas-${root.id || Math.random().toString(36).slice(2)}`;
  let canvas = document.getElementById(id);
  if (!canvas){
    canvas = document.createElement('canvas');
    canvas.id = id;
    document.body.appendChild(canvas);
  }
  canvas.classList.add('gp-crt-canvas');
  if (hideOnMobile) canvas.classList.add('is-mobile-hidden');

  // Defensive: make sure styling can’t be overridden by theme CSS
  canvas.style.zIndex = '-1';
  canvas.style.pointerEvents = 'none';
  canvas.style.position = 'fixed';
  canvas.style.inset = '0';

  return canvas;
}
